// The site's stickers (PLAN-2026-10-30-stickers, scenario I1): a message that
// is a picture, the writer's team's character in a pose with a word or two on
// it, sent with one tap.
//
// DATA, NOT DRAWINGS (FACES.md §2): each sticker is an id, its words, the mood
// its character wears and what goes with it, and the words that find it. How
// a pose or a burst of confetti looks is each client's own, so a second client
// offers the same stickers and draws them its own way.
//
// THE LIST ONLY GROWS. A sticker sent is kept by its id (`ChatCard`'s
// `sticker`), so an id is never removed or renamed; a client that meets an id
// it does not know draws the card's words.
//
// NOTHING TAUNTS (DIRECTION U18): the site never leads with trash talk, so the
// competitive row is *Game on!* and never *You're going down*.

import { z } from 'zod';
import type { FaceMood } from '../teams/faces';

/** What goes with a sticker's character, drawn around it by the client. */
export type StickerExtra =
  | 'confetti'
  | 'burst'
  | 'flames'
  | 'hearts'
  | 'stars'
  | 'sparkles'
  | 'sparks'
  | 'arrows'
  | 'questions'
  | 'shock'
  | 'sweat'
  | 'rain'
  | 'zzz'
  | 'none';

export interface Sticker {
  /** Lowercase words with dashes; kept forever once sent. */
  readonly id: string;
  /** What the sticker says, on it and to a screen reader. */
  readonly words: string;
  /** The mood its character wears, and so its pose. */
  readonly mood: FaceMood;
  readonly extra: StickerExtra;
}

/** The rows the tray draws, four to a row: celebrating, friendly, competing in fun, on the chin. */
export const STICKERS = [
  { id: 'lets-go', words: 'Let’s go!', mood: 'pleased', extra: 'confetti' },
  { id: 'boom', words: 'Boom!', mood: 'pleased', extra: 'burst' },
  { id: 'on-fire', words: 'On fire!', mood: 'pleased', extra: 'flames' },
  { id: 'love-it', words: 'Love it!', mood: 'pleased', extra: 'hearts' },
  { id: 'hey-all', words: 'Hey, all!', mood: 'waving', extra: 'none' },
  { id: 'thanks', words: 'Thanks!', mood: 'waving', extra: 'hearts' },
  { id: 'good-game', words: 'Good game', mood: 'waving', extra: 'stars' },
  { id: 'nice-one', words: 'Nice one!', mood: 'pleased', extra: 'sparkles' },
  { id: 'game-on', words: 'Game on!', mood: 'talking', extra: 'sparks' },
  { id: 'talk-trade', words: 'Let’s talk trade', mood: 'talking', extra: 'arrows' },
  { id: 'hmm', words: 'Hmm…', mood: 'thinking', extra: 'questions' },
  { id: 'whoa', words: 'Whoa!', mood: 'surprised', extra: 'shock' },
  { id: 'phew', words: 'Phew!', mood: 'pleased', extra: 'sweat' },
  { id: 'ouch', words: 'Ouch.', mood: 'annoyed', extra: 'sweat' },
  { id: 'rough-week', words: 'Rough week', mood: 'annoyed', extra: 'rain' },
  { id: 'zzz', words: 'Zzz', mood: 'asleep', extra: 'zzz' },
] as const satisfies readonly Sticker[];

export type StickerId = (typeof STICKERS)[number]['id'];

const IDS = STICKERS.map((s) => s.id) as [StickerId, ...StickerId[]];

/** A sticker a caller may send: one of the list. */
export const StickerIdSchema = z.enum(IDS, { error: 'There is no sticker by that name.' });

/** The sticker by its id, or undefined for one this build does not know. */
export function stickerOf(id: string): Sticker | undefined {
  return (STICKERS as readonly Sticker[]).find((s) => s.id === id);
}

/**
 * A sticker in words, a message's `body` (`0001` keeps a card's text there):
 * what a conversation's last line, a notification and a quote of it read.
 */
export function stickerSaid(words: string): string {
  return `Sticker: ${words}`;
}
